export type Role = 'parent' | 'child';
export type Frequency = 'daily' | 'weekly' | 'biweekly' | 'monthly';
export type OccurrenceState = 'done' | 'due_today' | 'overdue' | 'upcoming';

export interface Profile {
  id: number;
  name: string;
  role: Role;
  emoji: string;
  color: string;
}

export interface Child extends Profile {
  active: number;
  allowance_weekly: number;
  allowance_monthly: number;
  head_start_points: number;
  reminder_mode: 'smart' | 'fixed';
  qualify_threshold: number;
  payout_mode: 'all_or_nothing' | 'prorated';
  reminder_times: string;
  reminders_enabled: number;
  nudge_lead_days: number;
}

export interface Chore {
  id: number;
  child_id: number;
  title: string;
  notes: string;
  icon: string;
  frequency: Frequency;
  weekdays: string;
  due_weekday: number;
  due_day_of_month: number;
  anchor_date: string;
  points: number;
  counts_for_pay: number;
  active: number;
  starts_on: string | null;
  ends_on: string | null;
  sort_order: number;
}

export interface Occurrence {
  choreId: number;
  childId: number;
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  dueDate: string;
  points: number;
  countsForPay: boolean;
  title: string;
  icon: string;
  notes: string;
  frequency: Frequency;
  state: OccurrenceState;
  completedAt: string | null;
  completedBy: number | null;
  alsoMissed?: number;
}

export interface Payout {
  id: number;
  child_id: number;
  period_type: 'week' | 'month';
  period_key: string;
  status: 'paid' | 'withheld';
  amount: number;
  note: string;
  decided_by: number | null;
  decided_at: string;
}

export interface StreakSave {
  date: string;
  kind: 'shield' | 'repair' | 'parent';
}

export interface RepairState {
  date: string;
  missing: { choreId: number; periodKey: string; title: string; icon: string }[];
  todayMissing: number;
  streakAtStake: number;
}

export interface StreakInfo {
  days: number;
  best: number;
  todayHasChores: boolean;
  todayComplete: boolean;
  shields: number;
  nextMilestone: number | null;
  unseenSaves: StreakSave[];
  repair: RepairState | null;
}

export interface HouseStreak {
  days: number;
  best: number;
  waitingOn: string[];
  todayComplete: boolean;
  reward: string;
  toReward: number | null;
}

export interface Badge {
  id: number;
  child_id: number;
  kind: string;
  value: number;
  earned_on: string;
  seen: number;
}

export interface TickResult {
  ok: boolean;
  periodKey: string;
  done: boolean;
  selfStart: boolean;
  repaired: boolean;
  shieldEarned: boolean;
  badges: number[];
}

export interface PetAccessory {
  id: string;
  slot: 'head' | 'face' | 'neck' | 'aura';
  name: string;
  level: number;
}

export interface PetSpecies {
  id: string;
  name: string;
  blurb: string;
}

export interface Pet {
  species: string;
  name: string;
  xp: number;
  level: number;
  stage: number;
  xpIntoLevel: number;
  xpForNextLevel: number | null;
  equipped: Record<string, string>;
  unlocked: string[];
  accessories: PetAccessory[];
  playedToday: boolean;
  playTaps: number;
  recent: { amount: number; reason: string; created_at: string }[];
}

export interface Extra {
  id: number;
  child_id: number | null;
  title: string;
  notes: string;
  icon: string;
  pricing: 'fixed' | 'hourly';
  amount: number;
  needs_approval: number;
  active: number;
  sort_order: number;
}

export interface Claim {
  id: number;
  extra_id: number;
  child_id: number;
  note: string;
  minutes: number | null;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  claimed_on: string;
  claimed_at: string;
  decided_by: number | null;
  decided_at: string | null;
  decision_note: string;
  /** When it was handed over; null while still owed. */
  paid_at: string | null;
  paid_payout_id: number | null;
  title: string;
  icon: string;
  pricing: 'fixed' | 'hourly';
  child_name: string;
  child_emoji: string;
}

export interface PeriodSummary {
  childId: number;
  periodType: 'week' | 'month';
  periodKey: string;
  from: string;
  to: string;
  totalPoints: number;
  earnedPoints: number;
  totalCount: number;
  doneCount: number;
  missedCount: number;
  openCount: number;
  rate: number;
  threshold: number;
  headStart: number;
  turnPoints: number;
  pointsNeeded: number;
  qualifies: boolean;
  applicable: boolean;
  provisional: boolean;
  allowance: number;
  amount: number;
  extrasApproved: number;
  extrasUnpaid: number;
  extrasPending: number;
  extrasCount: number;
  totalAmount: number;
  currency: string;
  payout: Payout | null;
  items: Occurrence[];
}

export interface Board {
  child: Child;
  date: string;
  overdue: Occurrence[];
  dueToday: Occurrence[];
  soon: Occurrence[];
  restOfPeriod: Occurrence[];
  later: Occurrence[];
  doneToday: Occurrence[];
  week: PeriodSummary;
  month: PeriodSummary;
  quietToday: string | null;
  snoozedUntil: string | null;
  streak: StreakInfo;
  house: HouseStreak | null;
  bonusCoins: number;
  pet: Pet | null;
  payday: {
    periodType: 'week' | 'month';
    date: string;
    pointsToGo: number;
    qualifies: boolean;
    amount: number;
    currency: string;
  };
  words: QuestWords;
  turns: TurnJob[];
  extras: Extra[];
  recentClaims: Claim[];
}

/** A job the kids take in turns rather than one that falls due on a date. */
export interface TurnJob {
  id: number;
  title: string;
  notes: string;
  icon: string;
  points: number;
  nextChildId: number;
  nextName: string;
  standings: { childId: number; name: string; emoji: string; turns: number; ahead: number }[];
  recent: { id: number; childId: number; name: string; date: string; created_at: string }[];
}

/** What the kids' sections and cadences are called; parents set these. */
export interface QuestWords {
  late: string;
  today: string;
  soon: string;
  done: string;
  extras: string;
  daily: string;
  weekly: string;
  biweekly: string;
  monthly: string;
}

export interface OverviewChild {
  child: Child;
  quietToday: string | null;
  snoozedUntil: string | null;
  pendingClaims: number;
  streak: number;
  /** Shared jobs this child is up next for. */
  turnsUp: { id: number; title: string; icon: string }[];
  today: { due: number; done: number; overdue: number };
  week: Omit<PeriodSummary, 'items'>;
  month: Omit<PeriodSummary, 'items'>;
}

export interface QuietDay {
  id: number;
  child_id: number;
  label: string;
  kind: 'weekly' | 'date';
  weekday: number | null;
  date: string | null;
}

export interface Settings {
  householdName: string;
  timezone: string;
  currency: string;
  quietHoursStart: string;
  quietHoursEnd: string;
  parentReviewEnabled: boolean;
  parentReviewWeekday: number;
  parentReviewTime: string;
  parentMonthlyReviewEnabled: boolean;
  schoolQuietEnabled: boolean;
  schoolQuietStart: string;
  schoolQuietEnd: string;
  saveNudgeEnabled: boolean;
  saveNudgeTime: string;
  houseStreakEnabled: boolean;
  houseStreakReward: string;
  wordLate: string;
  wordToday: string;
  wordSoon: string;
  wordDone: string;
  wordExtras: string;
  wordDaily: string;
  wordWeekly: string;
  wordBiweekly: string;
  wordMonthly: string;
}

/** The settings keys holding kid-facing words, in the order the form shows them. */
export const WORD_FIELDS = [
  { key: 'wordToday', label: 'Due today' },
  { key: 'wordLate', label: 'Late' },
  { key: 'wordSoon', label: 'Coming up' },
  { key: 'wordDone', label: 'Finished today' },
  { key: 'wordExtras', label: 'Extra money' },
  { key: 'wordDaily', label: 'A daily chore' },
  { key: 'wordWeekly', label: 'A weekly chore' },
  { key: 'wordBiweekly', label: 'An every-two-weeks chore' },
  { key: 'wordMonthly', label: 'A monthly chore' },
] as const satisfies readonly { key: keyof Settings; label: string }[];

/** One day of a child's streak, for the parent's restore screen. */
export interface StreakDay {
  date: string;
  due: number;
  done: number;
  saved: 'shield' | 'repair' | 'parent' | null;
  sealed: boolean;
  breaksRun: boolean;
}

export interface StreakView {
  days: number;
  best: number;
  shields: number;
  recent: StreakDay[];
}
